import { useCallback, useMemo } from 'react';
import { useAuthContext } from '@/context/AuthContext.tsx';

export function useAnalytics() {
  const { user } = useAuthContext();

  const isAnalyticsEnabled = useMemo(() => {
    if (!user) return true;

    return !!user.consents?.analytics;
  }, [user]);

  const trackGaEvent = useCallback(
    ({ event, ...request }: any): void => {
      try {
        if (!window) {
          return;
        }
        if (!isAnalyticsEnabled) {
          return;
        }

        if (!window?.gtag) {
          console.error('GA dataLayer not found, cannot track event');
          return;
        }

        window.gtag('event', event, request);
      } catch (error) {
        console.error('Error tracking GA event:', error);
      }
    },
    [isAnalyticsEnabled]
  );

  const trackLimitReached = useCallback(
    (type: 'ai_responses' | 'username_search'): void => {
      trackGaEvent({
        event: 'limit_reached',
        type,
        remaining: 0,
        timestamp: Date.now(),
      });
    },
    [trackGaEvent]
  );

  const trackUpgradeModalOpened = useCallback(
    (source: 'limit_block' | 'header_button' | 'cta' | 'scroll' | 'subscriber_limit'): void => {
      trackGaEvent({
        event: 'upgrade_modal_opened',
        source,
        timestamp: Date.now(),
      });
    },
    [trackGaEvent]
  );

  return useMemo(
    () => ({
      trackGaEvent,
      trackLimitReached,
      trackUpgradeModalOpened,
    }),
    [trackGaEvent, trackLimitReached, trackUpgradeModalOpened]
  );
}
